import { supabase } from '@/integrations/supabase/client';

export interface RegisterAdminData {
  userId: string;
  telegramId: number;
  telegramUsername?: string;
  telegramFirstName: string;
  telegramLastName?: string;
  languageCode?: string;
}

export interface UpdateAdminData {
  telegram_username?: string;
  telegram_first_name?: string;
  telegram_last_name?: string;
  language_code?: string;
  notification_enabled?: boolean;
  is_active?: boolean;
}

export interface MessageLogFilters {
  direction?: 'incoming' | 'outgoing';
  adminId?: string;
  messageType?: string;
  dateFrom?: string;
  dateTo?: string;
  searchText?: string;
  page?: number;
  pageSize?: number;
}

export const telegramAdminService = {
  async getAdminUsers(page = 1, pageSize = 10, filters: any = {}) {
    let query = supabase
      .from('telegram_admin_users')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.search) {
      query = query.or(
        `telegram_username.ilike.%${filters.search}%,telegram_first_name.ilike.%${filters.search}%`
      );
    }

    if (filters.status !== undefined) {
      query = query.eq('is_active', filters.status);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;
    
    if (error) throw error;
    
    return { data, count };
  },

  async registerAdmin(data: RegisterAdminData) {
    const { data: result, error } = await supabase.functions.invoke(
      'telegram-admin-register',
      {
        body: data,
      }
    );

    if (error) throw error;
    return result;
  },

  async updateAdmin(adminId: string, data: UpdateAdminData) {
    const { data: result, error } = await supabase
      .from('telegram_admin_users')
      .update(data)
      .eq('id', adminId)
      .select()
      .single();

    if (error) throw error;
    return result;
  },

  async deactivateAdmin(adminId: string) {
    return this.updateAdmin(adminId, { is_active: false });
  },

  async activateAdmin(adminId: string) {
    return this.updateAdmin(adminId, { is_active: true });
  },

  async getMessageLogs(filters: MessageLogFilters = {}) {
    const pageSize = filters.pageSize || 20;
    const page = filters.page || 1;
    
    let query = supabase
      .from('telegram_message_log')
      .select('*, telegram_admin_users!inner(telegram_username, telegram_first_name)', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.direction) {
      query = query.eq('direction', filters.direction);
    }

    if (filters.adminId) {
      query = query.eq('telegram_admin_id', filters.adminId);
    }

    if (filters.messageType) {
      query = query.eq('message_type', filters.messageType);
    }

    if (filters.searchText) {
      query = query.ilike('message_text', `%${filters.searchText}%`);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;
    
    if (error) throw error;
    
    return { data, count };
  },

  async setupWebhook() {
    const { data, error } = await supabase.functions.invoke('telegram-setup-webhook');
    
    if (error) throw error;
    return data;
  },

  async getWebhookInfo() {
    const { data, error } = await supabase.functions.invoke('telegram-setup-webhook');
    
    if (error) throw error;
    return data?.webhookInfo;
  },

  async sendTestMessage(chatId: number, text: string, parseMode?: 'Markdown' | 'HTML') {
    const { data, error } = await supabase.functions.invoke('telegram-send-message', {
      body: {
        chatId,
        text,
        parseMode,
      },
    });

    if (error) throw error;
    return data;
  },

  async getAdmins() {
    const { data, error } = await supabase.functions.invoke('get-admin-users');
    
    if (error) throw error;
    return data;
  },
};
